import { useRef, useState } from "react"
import Form, { useForm } from "../../gui/Form/Form"
import FormControl from "../../gui/Form/FormControl"
import Validators from "../../gui/Form/Validators"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import AutoComplete from "../../gui/TextInput/AutoComplete"
import Holding from "../../models/Holding"
import Prices from "../../models/Prices"
import AccountSelect from "../widgets/AccountSelect"
import Button from "../../gui/Button/Button"
import TextInput, { Formatters, ITextInput } from "../../gui/TextInput/TextInput"
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker"
import TransTypeSelect from "../widgets/TransTypeSelect"
import { UpdateEvent } from "../../gui/GUIEvents"
import { Price } from "../../models/Price"
import Checkbox, { IToggleButton } from "../../gui/Button/Checkbox"
import { IDropdown } from "../../gui/Dropdown/Dropdown"
import usePortfolio from "../../hooks/PortfolioProvider"
import PortAccount from "../../models/PortAccount"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import { ETransactionType } from "../../shared/ITransaction"
import { usePath } from "../../libs/path/PathProvider"
import PortUtils from "../../portlibs/PortUtils"
import { TAddHoldingProps } from "./TAddHoldingProps"

export default function EquityDlg(props:TAddHoldingProps){
    const {transaction} = props
    const {portfolio} = usePortfolio()
    const {query} = usePath()
    const {account} = PortUtils.getFilter(query, 0)
    const holding = transaction?.holding ?? (props.holdingId ? portfolio.holding(props.holdingId)?.filterByAccount(account) : null)

    const [ticker,setTicker] = useState<string>(transaction?.holding?.id ?? props.holdingId ?? "")
    const [price,setPrice] = useState<string>(transaction?.price.toString() ?? Prices.i.get(ticker)?.price.toString() ?? "" )
    const [busy,setBusy] = useState<boolean>(false)
    const refAction = useRef<IDropdown<ETransactionType>|null>(null)
    const refQty = useRef<ITextInput|null>(null)
    const refAccount = useRef<IDropdown<number>|null>(null)
    const refPrice = useRef<ITextInput|null>(null)
    const refDate = useRef<IDatePicker|null>(null)
    const refMaturity = useRef<IDatePicker|null>(null)
    const refNote = useRef<ITextInput|null>(null)
    const refYield = useRef<ITextInput|null>(null)
    const refUpdateCash = useRef<IToggleButton|null>(null)
    const refTax = useRef<IToggleButton|null>(null)
    const {popOverlay} = useOverlay()
    const {valid} = useForm()
    const tickers = Prices.i.list
    const hasMaturity = Price.isBond(ticker) || Price.isGIC(ticker)
    const {addSnackbar} = useSnackbar()

    const onSave = async () => {
        if (busy) return
        else setBusy(false)

        const trans = Holding.cloneTransaction(props.transaction, holding, ticker)
        trans.accountId = refAccount.current!.value!
        trans.price = parseFloat(price)
        trans.quantity = parseFloat(refQty.current!.value!)
        trans.date = refDate.current!.value!
        trans.maturity = refMaturity.current!.value ?? trans.date
        trans.yield = parseFloat(refYield.current!.value || "0")
        trans.note = refNote.current!.value!
        trans.type = refAction.current!.value!
        try {
            await PortAccount.i.update(portfolio, trans, !!refUpdateCash.current?.checked, !!refTax.current?.checked)
            popOverlay(PortUtils.AddHoldingId)
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }

    const onTicker = (e:UpdateEvent<string>) => {
        const ticker = e.value || ""
        setTicker(ticker)
        
        const price = Prices.i.get(ticker)
        price && setPrice(price.price.toString())
    }
    const onPrice = (e:UpdateEvent<string>) => setPrice(e.value||"")

    return <>
        <div className="content">
            <FormControl validators={[Validators.Required("Ticker")]}>
                <AutoComplete value={ticker} onUpdate={onTicker} placeholder="Ticker" float={true} menuProps={{options:tickers}} canClear={true} anchorProps={{width:"match"}} inputProps={{readOnly:!!holding}}/>
            </FormControl>
            <div className="grid gap-16">
                <AccountSelect ref={refAccount} defaultValue={transaction?.accountId ?? account}/>
                <TransTypeSelect ref={refAction} filter="equity"/>
                <div className="flex gap-8">
                    <Checkbox ref={refUpdateCash}>Cash</Checkbox>
                    <Checkbox ref={refTax}>Tax</Checkbox>
                </div>
            </div>
            <div className="grid gap-16">
                <FormControl validators={[Validators.NonZeroNumber("Quantity")]}>
                    <TextInput ref={refQty} placeholder="Quantity" float={true} evaluator={Formatters.Evaluate} defaultValue={transaction?.quantity?.toString() ?? ""}/>
                </FormControl>
                <FormControl validators={[Validators.Number("Price")]}>
                    <TextInput ref={refPrice} placeholder="Price" float={true} evaluator={Formatters.Evaluate} value={price} onUpdate={onPrice}/>
                </FormControl>
            </div>
            <div className="grid gap-16">
                <FormControl validators={[Validators.Required("Date")]}>
                    <DatePicker ref={refDate} placeholder="Date" float={true} defaultValue={props.transaction?.date}/>
                </FormControl>
                <DatePicker ref={refMaturity} placeholder="Maturity" float={true} inputProps={{disabled:!hasMaturity}} defaultValue={props.transaction?.maturity}/>
                <TextInput ref={refYield} placeholder="Yield" float={true} evaluator={Formatters.Evaluate} defaultValue={props.transaction?.yield?.toString()}/>
            </div>
            <TextInput ref={refNote} placeholder="Notes" float={true} defaultValue={props.transaction?.note}/>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}
